import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { config } from './config';
import { clearStaleState, getUser, handleSignInCallback, signIn, signOut } from './auth';
import {
  DataIndexResponse,
  deleteTemplate,
  generateZip,
  getMe,
  listTemplates,
  uploadDataIndex,
  uploadTemplates,
} from './api';
import { connectStatusFeed } from './ws';
import type { StatusRecord } from './ws';
import type { Client } from '@stomp/stompjs';
import { TemplatesPage } from './pages/TemplatesPage';
import { GeneratePage } from './pages/GeneratePage';
import { LandingPage } from './pages/LandingPage';
import { AdminPage } from './pages/AdminPage';
import helpHtml from './assets/help.html?raw';

type Page = 'generate' | 'templates' | 'admin';

const emptyDataIndex: DataIndexResponse = {
  dataIndexId: '',
  lookupKeys: [],
  entries: [],
  columnHeaders: [],
};

const NAV_ITEMS: { id: Page; label: string; icon: string; adminOnly?: boolean }[] = [
  { id: 'generate', label: 'Generate', icon: 'pi pi-play' },
  { id: 'templates', label: 'Templates', icon: 'pi pi-file' },
  { id: 'admin', label: 'Admin', icon: 'pi pi-shield', adminOnly: true },
];

const PAGE_TITLES: Record<Page, string> = {
  generate: 'Generate',
  templates: 'Templates',
  admin: 'Admin',
};

function App() {
  const toastRef = useRef<Toast | null>(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activePage, setActivePage] = useState<Page>('generate');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [lockedByAdmin, setLockedByAdmin] = useState(false);
  const [refreshRulesKey, setRefreshRulesKey] = useState(0);
  const [adminActivityFeed, setAdminActivityFeed] = useState<import('./ws').StatusRecord[]>([]);
  const [templates, setTemplates] = useState<string[]>([]);
  const [lookupKey, setLookupKey] = useState(() => localStorage.getItem('lookupKey') ?? '');
  const [dataIndex, setDataIndex] = useState<DataIndexResponse>(emptyDataIndex);
  const [selectedEntries, setSelectedEntries] = useState<DataIndexResponse['entries']>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [statusFeed, setStatusFeed] = useState<StatusRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [entryFilter, setEntryFilter] = useState('');

  useEffect(() => {
    void clearStaleState();
    const url = new URL(window.location.href);
    if (url.pathname.endsWith('/auth/callback')) {
      handleSignInCallback()
        .then(user => {
          setUserEmail(user.profile.email || user.profile.preferred_username || user.profile.sub);
          window.history.replaceState({}, '', '/');
        })
        .catch(console.error);
      return;
    }
    getUser().then(user => {
      if (user) setUserEmail(user.profile.email || user.profile.preferred_username || user.profile.sub);
    });
  }, []);

  useEffect(() => {
    if (!userEmail) return;
    listTemplates().then(setTemplates).catch(error => notify('error', error.message));
    getMe().then(me => setIsAdmin(me.roles.includes('Odin'))).catch(() => {});
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    let client: Client | null = null;
    connectStatusFeed(record => {
      if (record.type === 'ADMIN_LOCK') {
        setLockedByAdmin(true);
      } else if (record.type === 'ADMIN_UNLOCK') {
        setLockedByAdmin(false);
      } else if (record.type === 'ADMIN_TEMPLATES_CHANGED') {
        setTemplates(record.payload as string[]);
      } else if (record.type === 'ADMIN_RULES_CHANGED') {
        setRefreshRulesKey(k => k + 1);
      } else if (record.type === 'USER_ACTIVITY') {
        setAdminActivityFeed(prev => [...prev, record].slice(-100));
      } else {
        setStatusFeed(prev => [...prev, record].slice(-50));
      }
    }).then(created => { client = created; });
    return () => { client?.deactivate(); };
  }, [userEmail]);

  const notify = (severity: 'success' | 'info' | 'warn' | 'error', detail: string) => {
    toastRef.current?.show({ severity, summary: severity.toUpperCase(), detail });
  };

  const handleTemplateUpload = async (files: File[]) => {
    try {
      await uploadTemplates(files);
      setTemplates(await listTemplates());
      notify('success', `${files.length} template${files.length > 1 ? 's' : ''} uploaded`);
    } catch (error: any) {
      notify('error', error.message);
    }
  };

  const handleDeleteTemplate = async (filename: string) => {
    try {
      await deleteTemplate(filename);
      setTemplates(prev => prev.filter(t => t !== filename));
      notify('success', 'Template deleted');
    } catch (error: any) {
      notify('error', error.message);
    }
  };

  const handleDataIndexUpload = async (files: File[]) => {
    if (!lookupKey) {
      notify('warn', 'Enter a lookup key before uploading.');
      return;
    }
    try {
      const response = await uploadDataIndex(files[0], lookupKey);
      localStorage.setItem('lookupKey', lookupKey);
      setDataIndex(response);
      setSelectedEntries([]);
      setEntryFilter('');
      notify('success', `Data index parsed — ${response.entries.length} entries`);
    } catch (error: any) {
      notify('error', error.message);
    }
  };

  const handleClearDataIndex = () => {
    setDataIndex(emptyDataIndex);
    setSelectedEntries([]);
    setEntryFilter('');
  };

  const handleGenerate = async () => {
    if (!dataIndex.dataIndexId) { notify('warn', 'Upload a data index first.'); return; }
    if (selectedEntries.length === 0 || selectedTemplates.length === 0) {
      notify('warn', 'Select at least one entry and one template.'); return;
    }
    setBusy(true);
    try {
      const payload = {
        dataIndexId: dataIndex.dataIndexId,
        lookupKey,
        commands: selectedEntries.map(entry => ({
          lookupKeyParts: entry.lookupKeyParts,
          templates: selectedTemplates,
        })),
      };
      const blob = await generateZip(payload);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'generated.zip';
      link.click();
      URL.revokeObjectURL(url);
      setDataIndex(emptyDataIndex);
      setSelectedEntries([]);
      setEntryFilter('');
      notify('success', 'Zip downloaded');
    } catch (error: any) {
      notify('error', error.message);
    } finally {
      setBusy(false);
    }
  };

  const configMissing = !config.oidc.authority || !config.oidc.clientId;

  if (!userEmail) {
    return (
      <>
        <Toast ref={t => (toastRef.current = t)} />
        <LandingPage onSignIn={signIn} />
      </>
    );
  }

  return (
    <div className="app-layout">
      <Toast ref={t => (toastRef.current = t)} />

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? '' : ' sidebar--collapsed'}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-title">
            <i className="pi pi-bolt" />
            <span className="sidebar-brand-text">Templater</span>
          </div>
          <Button
            className="sidebar-toggle"
            icon={sidebarOpen ? 'pi pi-chevron-left' : 'pi pi-chevron-right'}
            text
            rounded
            severity="secondary"
            onClick={() => setSidebarOpen(o => !o)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          />
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.filter(item => !item.adminOnly || isAdmin).map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item${activePage === item.id ? ' active' : ''}`}
              onClick={() => setActivePage(item.id)}
              title={sidebarOpen ? undefined : item.label}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Button
            icon="pi pi-cog"
            text
            rounded
            tooltip="OAuth2 config"
            tooltipOptions={{ position: 'right' }}
            onClick={() => setShowConfig(true)}
          />
        </div>
      </aside>

      {/* Main area */}
      <div className={`main-area${sidebarOpen ? '' : ' main-area--collapsed'}`}>
        <header className="topbar">
          <div className="topbar-left">
            <span className="topbar-title">{PAGE_TITLES[activePage]}</span>
          </div>
          <div className="flex align-items-center gap-2">
            <Button
              icon="pi pi-question-circle"
              text
              rounded
              severity="secondary"
              onClick={() => setShowHelp(true)}
              tooltip="Gebruikershandleiding"
              tooltipOptions={{ position: 'bottom' }}
              aria-label="Help"
            />
            <Tag value={userEmail ?? 'Not signed in'} severity={userEmail ? 'success' : 'warning'} />
            {userEmail
              ? <Button label="Sign out" icon="pi pi-sign-out" size="small" outlined onClick={() => signOut()} />
              : <Button label="Sign in" icon="pi pi-sign-in" size="small" onClick={() => signIn()} />
            }
          </div>
        </header>

        <main className="page-content">
          {configMissing && (
            <div className="config-warning">
              <strong>OAuth2 config missing.</strong> Set <code>VITE_OIDC_AUTHORITY</code> and{' '}
              <code>VITE_OIDC_CLIENT_ID</code> in <code>frontend/.env</code>.
            </div>
          )}

          {!userEmail ? null : (
            <>
              {activePage === 'generate' && (
                <GeneratePage
                  templates={templates}
                  lookupKey={lookupKey}
                  onLookupKeyChange={setLookupKey}
                  dataIndex={dataIndex}
                  selectedEntries={selectedEntries}
                  onSelectedEntriesChange={setSelectedEntries}
                  selectedTemplates={selectedTemplates}
                  onSelectedTemplatesChange={setSelectedTemplates}
                  entryFilter={entryFilter}
                  onEntryFilterChange={setEntryFilter}
                  statusFeed={statusFeed}
                  busy={busy}
                  onDataIndexUpload={handleDataIndexUpload}
                  onClearDataIndex={handleClearDataIndex}
                  onGenerate={handleGenerate}
                />
              )}
              {activePage === 'templates' && (
                <TemplatesPage
                  templates={templates}
                  columnHeaders={dataIndex.columnHeaders}
                  onUpload={handleTemplateUpload}
                  onDelete={handleDeleteTemplate}
                  locked={lockedByAdmin}
                  refreshRulesKey={refreshRulesKey}
                />
              )}
              {activePage === 'admin' && isAdmin && (
                <AdminPage activityFeed={adminActivityFeed} />
              )}
            </>
          )}
        </main>
      </div>

      <Dialog
        header="Gebruikershandleiding"
        visible={showHelp}
        onHide={() => setShowHelp(false)}
        style={{ width: '90vw', maxWidth: '1100px', height: '90vh' }}
        contentStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
        maximizable
      >
        <iframe
          srcDoc={helpHtml}
          style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
          title="Gebruikershandleiding"
          sandbox="allow-same-origin"
        />
      </Dialog>

      <Dialog
        header="OAuth2 Settings"
        visible={showConfig}
        onHide={() => setShowConfig(false)}
        style={{ width: '30rem' }}
      >
        <p className="m-0 mb-2">Configure in <code>frontend/.env</code>:</p>
        <ul className="m-0 pl-4">
          <li><code>VITE_OIDC_AUTHORITY</code></li>
          <li><code>VITE_OIDC_CLIENT_ID</code></li>
          <li><code>VITE_OIDC_REDIRECT_URI</code> <span className="text-500">(optional)</span></li>
          <li><code>VITE_OIDC_SCOPE</code> <span className="text-500">(optional)</span></li>
          <li><code>VITE_API_BASE</code> <span className="text-500">(optional)</span></li>
          <li><code>VITE_WS_BASE</code> <span className="text-500">(optional)</span></li>
        </ul>
      </Dialog>
    </div>
  );
}

export default App;
